import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import FormSection from './components/FormSection';
import FileUploadArea from './components/FileUploadArea';
import DataQualityIndicator from './components/DataQualityIndicator';
import CompletionChecklist from './components/CompletionChecklist';

const CompanyProfileForm = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    foundedYear: '',
    businessType: '',
    registrationNumber: '',
    currentCapacity: '',
    targetCapacity: '',
    productionUnit: 'liter',
    employeeCount: '',
    operationalYears: '',
    annualRevenue: '',
    businessLicense: null,
    taxDocument: null,
    ownershipDocument: null
  });

  const [errors, setErrors] = useState({});
  const [completedSections, setCompletedSections] = useState(0);

  const businessTypeOptions = [
    { value: 'cv', label: 'CV (Commanditaire Vennootschap)' },
    { value: 'pt', label: 'PT (Perseroan Terbatas)' },
    { value: 'koperasi', label: 'Koperasi' },
    { value: 'umkm', label: 'UMKM' },
    { value: 'perorangan', label: 'Perorangan' }
  ];

  const productionUnitOptions = [
    { value: 'liter', label: 'Liter/Hari' },
    { value: 'kg', label: 'Kilogram/Hari' },
    { value: 'ton', label: 'Ton/Bulan' }
  ];

  const checklistItems = [
    {
      label: 'Informasi Dasar Perusahaan',
      description: 'Nama, alamat, dan tahun berdiri',
      completed: formData?.companyName && formData?.address && formData?.foundedYear
    },
    {
      label: 'Data Registrasi Bisnis',
      description: 'Jenis usaha dan nomor registrasi',
      completed: formData?.businessType && formData?.registrationNumber
    },
    {
      label: 'Kapasitas Produksi',
      description: 'Kapasitas saat ini dan target',
      completed: formData?.currentCapacity && formData?.targetCapacity
    },
    {
      label: 'Data Operasional',
      description: 'Jumlah karyawan dan lama operasi',
      completed: formData?.employeeCount && formData?.operationalYears
    },
    {
      label: 'Informasi Keuangan',
      description: 'Pendapatan tahunan',
      completed: formData?.annualRevenue
    },
    {
      label: 'Dokumen Pendukung',
      description: 'Izin usaha dan dokumen pajak',
      completed: formData?.businessLicense && formData?.taxDocument
    }
  ];

  const completionPercentage = Math.round((checklistItems?.filter(item => item?.completed)?.length / checklistItems?.length) * 100);

  useEffect(() => {
    const completed = checklistItems?.filter(item => item?.completed)?.length;
    setCompletedSections(completed);
  }, [formData]);

  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (Object.values(formData)?.some(value => value)) {
        setIsSaving(true);
        setTimeout(() => {
          setIsSaving(false);
          setLastSaved(new Date());
        }, 1000);
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.companyName) newErrors.companyName = 'Nama perusahaan wajib diisi';
    if (!formData?.address) newErrors.address = 'Alamat wajib diisi';
    if (!formData?.foundedYear) newErrors.foundedYear = 'Tahun berdiri wajib diisi';
    if (!formData?.businessType) newErrors.businessType = 'Jenis usaha wajib dipilih';
    if (!formData?.registrationNumber) newErrors.registrationNumber = 'Nomor registrasi wajib diisi';
    if (!formData?.currentCapacity) newErrors.currentCapacity = 'Kapasitas saat ini wajib diisi';
    if (!formData?.targetCapacity) newErrors.targetCapacity = 'Kapasitas target wajib diisi';
    if (!formData?.employeeCount) newErrors.employeeCount = 'Jumlah karyawan wajib diisi';
    if (!formData?.operationalYears) newErrors.operationalYears = 'Lama operasi wajib diisi';
    if (!formData?.annualRevenue) newErrors.annualRevenue = 'Pendapatan tahunan wajib diisi';

    if (formData?.foundedYear && (formData?.foundedYear < 1900 || formData?.foundedYear > 2026)) {
      newErrors.foundedYear = 'Tahun berdiri tidak valid';
    }

    if (formData?.currentCapacity && parseFloat(formData?.currentCapacity) <= 0) {
      newErrors.currentCapacity = 'Kapasitas harus lebih dari 0';
    }

    if (formData?.targetCapacity && parseFloat(formData?.targetCapacity) <= 0) {
      newErrors.targetCapacity = 'Kapasitas target harus lebih dari 0';
    }

    if (formData?.currentCapacity && formData?.targetCapacity && 
        parseFloat(formData?.targetCapacity) < parseFloat(formData?.currentCapacity)) {
      newErrors.targetCapacity = 'Kapasitas target harus lebih besar dari kapasitas saat ini';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      setIsSaving(true);
      setTimeout(() => {
        setIsSaving(false);
        navigate('/supplier-management');
      }, 1500);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('id-ID')?.format(value);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressIndicator currentStep={1} completedSteps={[]} />
      <main className="pt-44 pb-16">
        <div className="container-executive">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Building2" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">Profil Perusahaan</h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Lengkapi informasi dasar perusahaan untuk analisis due diligence
                </p>
              </div>
            </div>

            {lastSaved && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-4">
                <Icon name="Check" size={16} color="var(--color-success)" />
                <span>Tersimpan otomatis pada {lastSaved?.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <FormSection
                  title="Informasi Dasar"
                  description="Data identitas dan lokasi perusahaan"
                  icon={<Icon name="FileText" size={24} color="var(--color-primary)" />}
                >
                  <Input
                    label="Nama Perusahaan"
                    type="text"
                    placeholder="PT Rumah Susu Yova"
                    value={formData?.companyName}
                    onChange={(e) => handleInputChange('companyName', e?.target?.value)}
                    error={errors?.companyName}
                    required
                  />

                  <Input
                    label="Alamat Lengkap"
                    type="text"
                    placeholder="Jl. Raya Industri No. 123, Kota"
                    value={formData?.address}
                    onChange={(e) => handleInputChange('address', e?.target?.value)}
                    error={errors?.address}
                    description="Alamat lokasi operasional utama"
                    required
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <Input
                      label="Tahun Berdiri"
                      type="number"
                      placeholder="2015"
                      value={formData?.foundedYear}
                      onChange={(e) => handleInputChange('foundedYear', e?.target?.value)}
                      error={errors?.foundedYear}
                      min="1900"
                      max="2026"
                      required
                    />

                    <Input
                      label="Lama Operasi (Tahun)"
                      type="number"
                      placeholder="9"
                      value={formData?.operationalYears}
                      onChange={(e) => handleInputChange('operationalYears', e?.target?.value)}
                      error={errors?.operationalYears}
                      description="Berapa tahun perusahaan beroperasi"
                      required
                    />
                  </div>
                </FormSection>

                <FormSection
                  title="Registrasi Bisnis"
                  description="Informasi legal dan status badan usaha"
                  icon={<Icon name="Shield" size={24} color="var(--color-primary)" />}
                >
                  <Select
                    label="Jenis Badan Usaha"
                    options={businessTypeOptions}
                    value={formData?.businessType}
                    onChange={(value) => handleInputChange('businessType', value)}
                    error={errors?.businessType}
                    placeholder="Pilih jenis badan usaha"
                    required
                  />

                  <Input
                    label="Nomor Registrasi/NIB"
                    type="text"
                    placeholder="1234567890123"
                    value={formData?.registrationNumber}
                    onChange={(e) => handleInputChange('registrationNumber', e?.target?.value)}
                    error={errors?.registrationNumber}
                    description="Nomor Induk Berusaha atau nomor registrasi resmi"
                    required
                  />

                  <Input
                    label="Jumlah Karyawan"
                    type="number"
                    placeholder="25"
                    value={formData?.employeeCount}
                    onChange={(e) => handleInputChange('employeeCount', e?.target?.value)}
                    error={errors?.employeeCount}
                    description="Total karyawan tetap dan kontrak"
                    required
                  />
                </FormSection>

                <FormSection
                  title="Kapasitas Produksi"
                  description="Data volume produksi untuk analisis scale-up"
                  icon={<Icon name="TrendingUp" size={24} color="var(--color-primary)" />}
                >
                  <Select
                    label="Satuan Produksi"
                    options={productionUnitOptions}
                    value={formData?.productionUnit}
                    onChange={(value) => handleInputChange('productionUnit', value)}
                    placeholder="Pilih satuan"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <Input
                      label="Kapasitas Saat Ini"
                      type="number"
                      placeholder="500"
                      value={formData?.currentCapacity}
                      onChange={(e) => handleInputChange('currentCapacity', e?.target?.value)}
                      error={errors?.currentCapacity}
                      description={`Dalam ${formData?.productionUnit || 'satuan'}`}
                      required
                    />

                    <Input
                      label="Kapasitas Target"
                      type="number"
                      placeholder="1500"
                      value={formData?.targetCapacity}
                      onChange={(e) => handleInputChange('targetCapacity', e?.target?.value)}
                      error={errors?.targetCapacity}
                      description={`Target scale-up dalam ${formData?.productionUnit || 'satuan'}`}
                      required
                    />
                  </div>

                  {formData?.currentCapacity && formData?.targetCapacity && (
                    <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
                      <div className="flex items-start gap-3">
                        <Icon name="Calculator" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-foreground mb-1">Proyeksi Pertumbuhan</p>
                          <p className="text-xs text-muted-foreground">
                            Peningkatan kapasitas: {Math.round(((parseFloat(formData?.targetCapacity) - parseFloat(formData?.currentCapacity)) / parseFloat(formData?.currentCapacity)) * 100)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </FormSection>

                <FormSection
                  title="Informasi Keuangan"
                  description="Data pendapatan untuk analisis finansial"
                  icon={<Icon name="DollarSign" size={24} color="var(--color-primary)" />}
                >
                  <Input
                    label="Pendapatan Tahunan (IDR)"
                    type="number"
                    placeholder="5000000000"
                    value={formData?.annualRevenue}
                    onChange={(e) => handleInputChange('annualRevenue', e?.target?.value)}
                    error={errors?.annualRevenue}
                    description="Pendapatan kotor tahun terakhir"
                    required
                  />

                  {formData?.annualRevenue && (
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Format Rupiah:</p>
                      <p className="text-lg font-semibold text-foreground">
                        Rp {formatCurrency(formData?.annualRevenue)}
                      </p>
                    </div>
                  )}
                </FormSection>

                <FormSection
                  title="Dokumen Pendukung"
                  description="Unggah dokumen legal dan perizinan"
                  icon={<Icon name="Upload" size={24} color="var(--color-primary)" />}
                >
                  <FileUploadArea
                    label="Izin Usaha"
                    description="Format: PDF, JPG, PNG (Maks. 5MB)"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(file) => handleInputChange('businessLicense', file)}
                    error={errors?.businessLicense}
                    required
                  />

                  <FileUploadArea
                    label="Dokumen Pajak (NPWP)"
                    description="Format: PDF, JPG, PNG (Maks. 5MB)"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(file) => handleInputChange('taxDocument', file)}
                    error={errors?.taxDocument}
                    required
                  />

                  <FileUploadArea
                    label="Dokumen Kepemilikan (Opsional)"
                    description="Akta pendirian atau dokumen kepemilikan lainnya"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(file) => handleInputChange('ownershipDocument', file)}
                    error={errors?.ownershipDocument}
                    required={false}
                  />
                </FormSection>

                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/login-dashboard')}
                    iconName="ArrowLeft"
                    iconPosition="left"
                    className="sm:w-auto"
                  >
                    Kembali
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="default"
                    loading={isSaving}
                    iconName="ArrowRight"
                    iconPosition="right"
                    fullWidth
                    className="sm:flex-1"
                  >
                    {isSaving ? 'Menyimpan...' : 'Simpan & Lanjutkan'}
                  </Button>
                </div>
              </form>
            </div>

            <div className="lg:col-span-1">
              <DataQualityIndicator
                completionPercentage={completionPercentage}
                completedSections={completedSections}
                totalSections={checklistItems?.length}
              />

              <CompletionChecklist items={checklistItems} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CompanyProfileForm;